import type { PostKind } from '@algoboo/schema'
import { cx } from './cx.js'

/**
 * Kind chips are monochrome outlines; the kind's hue lives only in the 6px
 * muted dot, and the label is always present (never colour alone).
 */
export const KIND_STYLE: Record<PostKind, { label: string; dot: string }> = {
  observation: { label: 'Observation', dot: 'bg-sky-deep' },
  decision: { label: 'Decision', dot: 'bg-lilac-deep' },
  trade: { label: 'Trade', dot: 'bg-peach-deep' },
  performance: { label: 'Performance', dot: 'bg-mint-deep' },
  launch: { label: 'Launch', dot: 'bg-butter-deep' },
  revision: { label: 'Revision', dot: 'bg-rose-deep' },
  note: { label: 'Note', dot: 'bg-ivory-deep' },
}

export function KindChip({ kind, className, size = 'md' }: { kind: PostKind; className?: string; size?: 'sm' | 'md' }) {
  const s = KIND_STYLE[kind]
  return (
    <span
      data-kind={kind}
      className={cx(
        'inline-flex items-center gap-1.5 rounded-[6px] border border-line-strong text-[11px] font-medium uppercase leading-none tracking-[0.06em] text-ink-2',
        size === 'sm' ? 'h-5 px-1.5' : 'h-6 px-2',
        className,
      )}
    >
      <span aria-hidden="true" className={cx('h-1.5 w-1.5 shrink-0 rounded-full', s.dot)} />
      {s.label}
    </span>
  )
}

/**
 * Milestone posts (kind `note` with `payload.type === 'milestone'`, written by the runtime's `social.milestones` job)
 * get their own chip: same outline, the accent dot, and the milestone's short label when it has one.
 */
export function MilestoneChip({ label, className, size = 'md' }: { label?: string | null; className?: string; size?: 'sm' | 'md' }) {
  return (
    <span
      data-kind="milestone"
      data-testid="milestone-chip"
      className={cx(
        'inline-flex items-center gap-1.5 rounded-[6px] border border-line-strong text-[11px] font-medium uppercase leading-none tracking-[0.06em] text-ink',
        size === 'sm' ? 'h-5 px-1.5' : 'h-6 px-2',
        className,
      )}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
      Milestone
      {label && <span className="font-mono normal-case tracking-normal text-ink-3">· {label}</span>}
    </span>
  )
}

/** A milestone post: kind `note` whose payload says `type: 'milestone'`. */
export function isMilestonePost(item: { kind: string; payload: unknown }): boolean {
  return item.kind === 'note' && !!item.payload && typeof item.payload === 'object' && (item.payload as { type?: unknown }).type === 'milestone'
}

/** Filter chip (toggle button): outline; pressed = neutral fill, the kind's dot lights up. */
export function KindFilterChip({ kind, pressed, onToggle, count }: { kind: PostKind | 'all'; pressed: boolean; onToggle: () => void; count?: number }) {
  const s = kind === 'all' ? { label: 'Everything', dot: 'bg-accent' } : KIND_STYLE[kind]
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onToggle}
      className={cx(
        'rh-press inline-flex h-8 items-center gap-2 rounded-[8px] border px-3 text-[13px] font-medium',
        pressed ? 'border-line-strong bg-surface-2 text-ink' : 'border-line text-ink-3 hover:border-line-strong hover:text-ink',
      )}
    >
      <span aria-hidden="true" className={cx('h-1.5 w-1.5 rounded-full', pressed ? s.dot : 'bg-ink-4')} />
      {s.label}
      {count !== undefined && <span className="font-mono text-[11px] tabular-nums text-ink-3">{count}</span>}
    </button>
  )
}
